package org.lennycodes.hldi.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.css.textAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.components.GradientButton
import org.lennycodes.hldi.components.VolunteerForm
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.layouts.MainLayout
import org.lennycodes.hldi.styles.*

@Composable
@Page("/get-involved")
fun GetInvolvedPage() {
    MainLayout {
        // Hero
                Section(
                    attrs = Modifier.padding(50.px, 20.px).then( AboutHeroSectionStyle.toModifier())
                        .toAttrs()
                ) {
                    H1(attrs = AboutHeroTitleStyle.toAttrs()) {
                        Text("Get Involved")
                    }
                    P(
                        attrs = AboutHeroTextStyle.toAttrs {
                            style { maxWidth(700.px) } // wider than the shared 600px default for this section's longer copy
                        }
                    ) {
                        Text("Meet the dedicated team of professionals and volunteers working tirelessly to protect lives and restore dignity across Tanzania.")
                    }
                    SectionWaveSvg()
                }

        // Volunteer Section
        Section(
            attrs = {
                id("volunteer")
                style {
                    backgroundColor(HLDITheme.Colors.white)
                }
            }
        ) {
            H2(
                attrs = {
                    style {
                        marginTop(75.px)
                        fontSize(36.px)
                        color(HLDITheme.Colors.primary)
                        textAlign(TextAlign.Center)
                        marginBottom(16.px)
                        fontFamily("Playfair Display", "serif")
                    }
                }
            ) {
                Text("Volunteer With Us")
            }
            P(
                attrs = {
                    style {
                        fontSize(16.px)
                        color(HLDITheme.Colors.mediumGray)
                        textAlign(TextAlign.Center)
                        marginBottom(48.px)
                    }
                }
            ) {
                Text("Share your skills, time, and passion to make a direct impact in communities.")
            }

            VolunteerForm()
        }

        // Donation Section
        Section(
            attrs = {
                id("donate")
            }
        ) {
            H2(
                attrs = {
                    style {
                        fontSize(36.px)
                        color(HLDITheme.Colors.primary)
                        textAlign(TextAlign.Center)
                        marginBottom(16.px)
                        fontFamily("Playfair Display", "serif")
                    }
                }
            ) {
                Text("Make a Donation")
            }
            P(
                attrs = {
                    style {
                        fontSize(16.px)
                        color(HLDITheme.Colors.mediumGray)
                        textAlign(TextAlign.Center)
                        marginBottom(48.px)
                    }
                }
            ) {
                Text("Your contribution helps us continue our vital work protecting lives and restoring dignity.")
            }

            // Donation Tiers
            SimpleGrid(
                numColumns = ResponsiveValues(
                    1, // base (xs)
                    1, // sm
                    2, // md
                    3,  // lg+
                    4,
                    5
                ),
                modifier = Modifier.gap(24.px).display(DisplayStyle.Flex)
                    .flexWrap(FlexWrap.Wrap)
                    .justifyContent(JustifyContent.Center)
                    .
                margin(bottom = 48.px)
            ) {
                listOf(
                    Triple("Supporter", "50,000 TZS", "Provides clean water for a family for one month"),
                    Triple("Advocate", "100,000 TZS", "Supports a child's education for a school term"),
                    Triple("Champion", "500,000 TZS", "Funds a community health camp serving 500 people")
                ).forEach { (tier, amount, impact) ->
                    Div(
                       attrs = Modifier.padding(40.px).then(SupporterCardStyle.toModifier())
                           .toAttrs()
                    ) {
                        H3(
                            attrs = {
                                style {
                                    fontSize(24.px)
                                    color(HLDITheme.Colors.primary)
                                    marginBottom(8.px)
                                    fontFamily("Playfair Display", "serif")
                                }
                            }
                        ) {
                            Text(tier)
                        }
                        Div(
                            attrs = {
                                style {
                                    fontSize(28.px)
                                    fontWeight(700)
                                    color(HLDITheme.Colors.secondary)
                                    marginBottom(16.px)
                                }
                            }
                        ) {
                            Text(amount)
                        }
                        P(
                            attrs = {
                                style {
                                    fontSize(14.px)
                                    color(HLDITheme.Colors.mediumGray)
                                    marginBottom(24.px)
                                    lineHeight("1.6")
                                }
                            }
                        ) {
                            Text(impact)
                        }
                        GradientButton("Donate", "#", modifier = ButtonStyle.toModifier())
                    }
                }
            }
                 //Payment Methods
            Div(attrs = Modifier  .padding(40.px).then( PaymentMethodsCardStyle.toModifier()).toAttrs()) {
                H3(attrs = PaymentMethodsHeadingStyle.toModifier().toAttrs()) {
                    Text("Payment Methods")
                }
                SimpleGrid(
                    numColumns = ResponsiveValues(1, 1, 2, 3, 4, 5),
                    modifier = Modifier.gap(32.px)
                ) {
                    PaymentMethodBlock("📱", "Mobile Money", mobileMoneyDetails)
                    PaymentMethodBlock("🏦", "Bank Transfer", bankDetails)
                }
            }
        }

        // Partnerships Section
        Section(
            attrs = {
                id("partnership")
                style {
                    backgroundColor(HLDITheme.Colors.background)
                }
            }
        ) {
            H2(
                attrs = {
                    style {
                        padding(16.px)
                        fontSize(36.px)
                        color(HLDITheme.Colors.primary)
                        textAlign(TextAlign.Center)
                        marginBottom(16.px)
                        fontFamily("Playfair Display", "serif")
                    }
                }
            ) {
                Text("Partner With Us")
            }
            P(
                attrs = {
                    style {
                        fontSize(16.px)
                        color(HLDITheme.Colors.mediumGray)
                        textAlign(TextAlign.Center)
                        marginBottom(48.px)
                    }
                }
            ) {
                Text("We collaborate with organizations that share our vision of protecting human dignity.")
            }

            SimpleGrid(
                numColumns = ResponsiveValues(
                    1, // base (xs)
                    1, // sm
                    2, // md
                    3,  // lg+
                    4,
                    5
                ),
                modifier = Modifier.gap(24.px)
                    .display(DisplayStyle.Flex)
                    .flexWrap(FlexWrap.Wrap)
                    .justifyContent(JustifyContent.Center)

            ) {
                listOf(
                    Triple("NGOs", "🤝", "Partner with us to implement joint programs and reach more communities in need."),
                    Triple("Companies", "🏢", "Corporate partnerships for CSR initiatives, employee volunteering, and sponsorships."),
                    Triple("Donors", "❤️", "Individual and institutional donors providing crucial funding for our programs.")
                ).forEach { (title, icon, description) ->
                    Div(
                        attrs = Modifier .padding(32.px)
                            .margin(bottom = 16.px).then(PartnerCardStyle.toModifier()).toAttrs()
                    ) {
                        Div(
                            attrs = {
                                style {
                                    fontSize(40.px)
                                    marginBottom(16.px)
                                }
                            }
                        ) {
                            Text(icon)
                        }
                        H3(
                            attrs = {
                                style {
                                    fontSize(20.px)
                                    color(HLDITheme.Colors.primary)
                                    marginBottom(12.px)
                                }
                            }
                        ) {
                            Text(title)
                        }
                        P(
                            attrs = {
                                style {
                                    fontSize(14.px)
                                    color(HLDITheme.Colors.mediumGray)
                                    lineHeight("1.6")
                                }
                            }
                        ) {
                            Text(description)
                        }
                    }
                }
            }
        }
    }
}
data class PaymentDetail(val label: String, val value: String)

val mobileMoneyDetails = listOf(
    PaymentDetail("M-Pesa", "+255 XXX XXX XXX"),
    PaymentDetail("Tigo Pesa", "+255 XXX XXX XXX"),
    PaymentDetail("Airtel Money", "+255 XXX XXX XXX")
)

val bankDetails = listOf(
    PaymentDetail("Bank", "XXXX Bank"),
    PaymentDetail("Account Name", "Human Life Defence Initiative"),
    PaymentDetail("Account Number", "XXXXXXXXXXXX"),
)

@Composable
fun PaymentMethodBlock(icon: String, title: String, details: List<PaymentDetail>) {
    Div(attrs = Modifier .padding(left = 20.px).then(PaymentMethodBlockStyle.toModifier()).toAttrs()) {
        H4(attrs = PaymentMethodHeadingStyle.toModifier().toAttrs()) {
            Text("$icon $title")
        }
        details.forEach { detail ->
            Div(attrs = Modifier.padding(top = 10.px, bottom = 10.px).then(PaymentRowStyle.toModifier()).toAttrs()) {
                P(attrs = PaymentLabelStyle.toModifier().toAttrs()) { Text(detail.label) }
                P(attrs = PaymentValueStyle.toModifier().toAttrs()) { Text(detail.value) }
            }
        }
    }
}